import { effect, inject, Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';

import { A11yAnnouncerService } from './a11y-announcer.service';

export const PAGE_TITLE_APP_NAME = 'CIPO® Software';

/**
 * Sets the document title (WCAG 2.4.2) and announces it to screen readers
 * (WCAG 4.1.3). Identical consecutive names are announced once.
 */
@Injectable({ providedIn: 'root' })
export class PageTitleService {
  private readonly title = inject(Title);
  private readonly announcer = inject(A11yAnnouncerService);

  private lastAnnounced: string = null;

  setPageTitle(pageName: string): void {
    // Empty name → keep current title (hybrid-app safety: AngularJS owns its title).
    if (!pageName) {
      return;
    }
    this.title.setTitle(`${PAGE_TITLE_APP_NAME} - ${pageName}`);
    if (pageName !== this.lastAnnounced) {
      this.lastAnnounced = pageName;
      this.announcer.announce(pageName);
    }
  }

  resetAppTitle(): void {
    this.title.setTitle(PAGE_TITLE_APP_NAME);
    this.lastAnnounced = null;
  }
}

/**
 * Binds the page title to a menu item, for module pages with no route `title`.
 * Call from a component's injection context.
 *
 * Uses the raw menu item `name` from the API (tenant-defined in DB), not i18n keys —
 * no language-change re-application is needed for module titles.
 *
 * Return `undefined` from `getItem()` to skip title management (e.g. AngularJS routes).
 * Return `null` or an item without `name` to reset to the default app title.
 */
export function bindPageTitle(getItem: () => { name?: string } | null | undefined): void {
  const pageTitle = inject(PageTitleService);

  effect(() => {
    const item = getItem();
    if (item === undefined) {
      return;
    }
    if (item?.name) {
      pageTitle.setPageTitle(item.name);
    } else {
      pageTitle.resetAppTitle();
    }
  });
}
